import { useState, useEffect } from 'react';
import { Filter, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import GapCard from './GapCard';
import GapResolutionDialog from './GapResolutionDialog';
import CompletenessProgressHeader from './CompletenessProgressHeader';
import {
  CompletenessGap,
  CompletenessProgress,
  GapActionType,
  GapStatus,
  GapCategory,
  GapAnalysisResponse,
} from '../../types/completenessGap';

interface CompletenessGapManagerProps {
  caseId: string;
}

export default function CompletenessGapManager({  caseId }: CompletenessGapManagerProps) {
  const [gaps, setGaps] = useState<CompletenessGap[]>([]);
  const [progress, setProgress] = useState<CompletenessProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<GapStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<GapCategory | 'all'>('all');

  // Dialog states
  const [selectedGap, setSelectedGap] = useState<CompletenessGap | null>(null);
  const [isResolutionDialogOpen, setIsResolutionDialogOpen] = useState(false);

  // Load gaps and progress
  useEffect(() => {
    if (caseId) {
      loadData();
    }
  }, [caseId, statusFilter, categoryFilter]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load gaps with filters
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);

      const [gapsResponse, progressResponse] = await Promise.all([
        api.get(`/completeness-gaps/cases/${ caseId}/gaps?${params.toString()}`),
        api.get(`/completeness-gaps/cases/${ caseId}/progress`),
      ]);

      setGaps(gapsResponse.gaps || []);
      setProgress(progressResponse);
    } catch (error: any) {
      console.error('Error loading completeness data:', error);
      toast.error('Failed to load completeness gaps');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleRecalculateScore = async () => {
    try {
      setIsRecalculating(true);
      toast.loading('Recalculating completeness score...', { id: 'recalculate' });
      await api.post(`/completeness-gaps/cases/${ caseId}/recalculate-score`);
      await loadData();
      toast.success('Completeness score recalculated successfully', { id: 'recalculate' });
    } catch (error: any) {
      console.error('Error recalculating score:', error);
      toast.error(
        error.response?.data?.detail || 'Failed to recalculate score. Please try again.',
        { id: 'recalculate' }
      );
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleAnalyzeCompleteness = async () => {
    try {
      setIsAnalyzing(true);
      toast.loading('Analyzing completeness with AI...', { id: 'analyze-completeness' });

      const response = await api.post(`/completeness-gaps/cases/${ caseId}/analyze-completeness`);

      await loadData();

      const gapsCreated = response.gapsCreated || 0;
      toast.success(
        `Completeness analysis complete. Identified ${gapsCreated} ${gapsCreated === 1 ? 'gap' : 'gaps'}.`,
        { id: 'analyze-completeness' }
      );
    } catch (error: any) {
      console.error('Error analyzing completeness:', error);
      toast.error(
        error.response?.data?.detail || 'Failed to analyze completeness. Please try again.',
        { id: 'analyze-completeness' }
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyze = (gapId: string) => {
    const gap = gaps.find((g) => g.gapId === gapId);
    if (gap) {
      setSelectedGap(gap);
      setIsResolutionDialogOpen(true);
    }
  };

  const handleResolveClick = (gapId: string) => {
    const gap = gaps.find((g) => g.gapId === gapId);
    if (gap) {
      setSelectedGap(gap);
      setIsResolutionDialogOpen(true);
    }
  };

  const handleDismissClick = async (gapId: string) => {
    const gap = gaps.find((g) => g.gapId === gapId);
    if (!gap) return;

    const reason = prompt('Enter dismissal reason:');
    if (!reason) return;

    try {
      toast.loading('Dismissing gap...', { id: 'dismiss' });
      await api.post(`/completeness-gaps/gaps/${gapId}/dismiss?dismissal_reason=${encodeURIComponent(reason)}`);
      await loadData();
      toast.success('Gap dismissed', { id: 'dismiss' });
    } catch (error: any) {
      console.error('Error dismissing gap:', error);
      toast.error('Failed to dismiss gap', { id: 'dismiss' });
    }
  };

  const handleReopen = async (gapId: string) => {
    try {
      toast.loading('Reopening gap...', { id: 'reopen' });
      await api.post(`/completeness-gaps/gaps/${gapId}/reopen`);
      await loadData();
      toast.success('Gap reopened', { id: 'reopen' });
    } catch (error: any) {
      console.error('Error reopening gap:', error);
      toast.error('Failed to reopen gap', { id: 'reopen' });
    }
  };

  const handleAnalyzeInDialog = async (
    gapId: string,
    userInput?: string
  ): Promise<GapAnalysisResponse | null> => {
    try {
      toast.loading('Analyzing gap with AI...', { id: 'analyze' });

      const params = new URLSearchParams();
      if (userInput) params.append('user_input', userInput);

      const response = await api.post(
        `/completeness-gaps/gaps/${gapId}/analyze?${params.toString()}`
      );

      toast.success('Analysis complete', { id: 'analyze' });
      return response;
    } catch (error: any) {
      console.error('Error analyzing gap:', error);
      toast.error('Failed to analyze gap', { id: 'analyze' });
      return null;
    }
  };

  const handleResolve = async (
    gapId: string,
    actionType: GapActionType,
    userInput?: string,
    dismissalReason?: string
  ) => {
    try {
      toast.loading('Resolving gap with Business Transformation Studio...', { id: 'resolve' });

      await api.post(`/completeness-gaps/gaps/${gapId}/resolve`, {
        actionType,
        userInput,
        dismissalReason,
      });

      await loadData();
      toast.success('Gap resolved successfully', { id: 'resolve' });
    } catch (error: any) {
      console.error('Error resolving gap:', error);
      toast.error('Failed to resolve gap', { id: 'resolve' });
      throw error;
    }
  };

  const handleCreateGapsFromAnalysis = async (gapId: string, analysis: any) => {
    try {
      toast.loading('Creating individual gaps...', { id: 'create-gaps' });

      const response = await api.post(`/completeness-gaps/gaps/${gapId}/create-from-analysis`, {
        analysis
      });

      await loadData();
      toast.success(`Created ${response.createdGaps} individual gaps from analysis`, { id: 'create-gaps' });
    } catch (error: any) {
      console.error('Error creating gaps from analysis:', error);
      toast.error('Failed to create gaps from analysis', { id: 'create-gaps' });
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="text-sm text-neutral-600 mt-3">
            Loading completeness gaps...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      {progress && (
        <CompletenessProgressHeader
          progress={progress}
          onAnalyze={handleAnalyzeCompleteness}
          isAnalyzing={isAnalyzing}
        />
      )}

      {/* Filters and Actions */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-neutral-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as GapStatus | 'all')}
            className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as GapCategory | 'all')}
            className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="why_not_100">Why Not 100%</option>
            <option value="missing_data">Missing Data</option>
            <option value="clarification">Clarification</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecalculateScore}
            loading={isRecalculating}
            disabled={isRecalculating}
          >
            {isRecalculating ? 'Recalculating...' : 'Recalculate Score'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            loading={isRefreshing}
            disabled={isRefreshing}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Gaps List */}
      {gaps.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
          <p className="text-neutral-600">
            {statusFilter !== 'all' || categoryFilter !== 'all'
              ? 'No gaps match the selected filters'
              : 'No completeness gaps found'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {gaps.map((gap) => (
            <GapCard
              key={gap.gapId}
              gap={gap}
              onAnalyze={handleAnalyze}
              onResolve={handleResolveClick}
              onDismiss={handleDismissClick}
              onReopen={handleReopen}
            />
          ))}
        </div>
      )}

      {/* Resolution Dialog */}
      <GapResolutionDialog
        gap={selectedGap}
        isOpen={isResolutionDialogOpen}
        onClose={() => {
          setIsResolutionDialogOpen(false);
          setSelectedGap(null);
        }}
        onResolve={handleResolve}
        onAnalyze={handleAnalyzeInDialog}
        onCreateGapsFromAnalysis={handleCreateGapsFromAnalysis}
      />
    </div>
  );
}
